import { memo } from 'react';

import { useIsWorkspaceAgentPanelAvailable } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useIsWorkspaceAgentPanelAvailable';
import { usePrivateStripWorkspaceAgentMarketingParamsWithoutAccess } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateStripWorkspaceAgentMarketingParamsWithoutAccess';
import { PrivateWorkspaceAgentPanelAffordance } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelAffordance';
import { PrivateWorkspaceAgentPanelProvider } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { PrivateUploadedBatchOffer } from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/PrivateUploadedBatchOffer';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export type PrivateWorkspaceAgentPanelProps = {
  /** The canvas editor has its own agent chat, so this panel steps aside while it is open. */
  canvasOpen: boolean;
};

export const PrivateWorkspaceAgentPanel = memo(({ canvasOpen }: PrivateWorkspaceAgentPanelProps) => {
  const { isWorkspaceAgentPanelProviderEnabled } = useIsWorkspaceAgentPanelAvailable();
  const { currentWorkspace } = useCurrentWorkspace();

  usePrivateStripWorkspaceAgentMarketingParamsWithoutAccess();

  if (!isWorkspaceAgentPanelProviderEnabled || !currentWorkspace) {
    return null;
  }

  return (
    // Switching workspace is a client-side transition under a persistent layout, so without a key the
    // provider would go on serving the previous workspace's session, thread and approval prompts while
    // every request it makes is scoped to the new one. Everything it owns belongs to one workspace, so
    // remounting is the whole reset — and stays the whole reset as more state is added here.
    <PrivateWorkspaceAgentPanelProvider key={currentWorkspace.id} workspaceId={currentWorkspace.id}>
      <PrivateUploadedBatchOffer />
      <PrivateWorkspaceAgentPanelAffordance canvasOpen={canvasOpen} />
    </PrivateWorkspaceAgentPanelProvider>
  );
});

PrivateWorkspaceAgentPanel.displayName = 'PrivateWorkspaceAgentPanel';
